import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";
import { LegalFooter } from "@/components/brand/LegalFooter";
import { RouteTopBar } from "@/components/layout/RouteTopBar";
import { BrandSlogan } from "@/components/brand/BrandSlogan";
import { signInEmail, signInGoogle, resendVerification } from "@/lib/auth/authClient";
import { translateAuthError } from "@/lib/auth/authErrorTranslator";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Login · VerseSport — Inteligencia Deportiva en Tiempo Real" },
      { name: "description", content: "Accede a VerseSport — Inteligencia Deportiva en Tiempo Real." },
    ],
  }),
  // P0-AUTH-10 — accept ?from= so we can return the user to the route they
  // were trying to reach before the unauth guard sent them here.
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search.from === "string" ? search.from : undefined,
  }),
  // SECURITY: if credentials ever land in the URL (native GET submit before
  // hydration, or a user pasting such a link), scrub them from the address
  // bar and history immediately so they don't leak into logs/analytics.
  beforeLoad: () => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.has("password") || url.searchParams.has("email")) {
      url.searchParams.delete("password");
      url.searchParams.delete("email");
      window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + url.hash);
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });
  const safeFrom = search.from && search.from.startsWith("/") && !search.from.startsWith("//")
    ? search.from
    : "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    void trackEvent("public_login_cta_clicked", { surface: "login_page" });
  }, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error(t("login_error"));
      return;
    }
    setSubmitting(true);
    try {
      await signInEmail(email.trim().toLowerCase(), password);
      void trackEvent("public_login_cta_clicked", { surface: "login_form", email_domain: email.split("@")[1] ?? "unknown" });
      toast.success(t("login_success"));
      void navigate({ to: safeFrom });
    } catch (err) {
      console.warn("[login] error", err);
      const rawLower = (err instanceof Error ? err.message : "").toLowerCase();
      const msg = translateAuthError(err, lang);
      const lower = rawLower;
      if (lower.includes("not confirmed") || lower.includes("email") && lower.includes("confirm")) {
        setNeedsVerification(true);
      }
      // If translator + raw message yielded nothing useful, surface the error
      // shape so the user (and QA) can see WHY login failed instead of the
      // generic "Ocurrió un error".
      const finalMsg = msg && msg.length > 4
        ? msg
        : (() => {
            try {
              if (err && typeof err === "object") {
                const e = err as { name?: string; status?: number; code?: string; message?: string };
                const parts = [e.name, e.code, e.status ? `HTTP ${e.status}` : null, e.message]
                  .filter(Boolean)
                  .join(" · ");
                return parts || JSON.stringify(err);
              }
              return String(err);
            } catch {
              return "Login error (sin detalle)";
            }
          })();
      toast.error(finalMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    if (!email.trim()) {
      toast.error("Ingresa tu email primero");
      return;
    }
    setResending(true);
    try {
      await resendVerification(email.trim().toLowerCase());
      toast.success("Email de verificación reenviado");
    } catch (e) {
      toast.error(translateAuthError(e, lang));
    } finally {
      setResending(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <RouteTopBar fallback="/welcome" />
      <div className="px-4 py-8 sm:px-6">
        <div className="mx-auto max-w-xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-primary">
            {t("fan_badge_personal")}
          </span>
          <h1 className="mt-2 text-2xl font-black text-foreground">{t("login_title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("login_sub")}</p>
          <BrandSlogan section="login" size="sm" align="left" withProduct className="mt-2" />

          <form onSubmit={onSubmit} method="post" action="/login" className="mt-6 space-y-4">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("login_email")}</span>
              <input
                name="email"
                type="email"
                required
                maxLength={255}
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("login_password_label")}</span>
              <input
                name="password"
                type="password"
                required
                minLength={6}
                maxLength={128}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground shadow disabled:opacity-60"
            >
              {submitting ? "..." : t("login_submit")}
            </button>
          </form>
          {needsVerification && (
            <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground">
              <p className="font-bold">{t("login_verify_required")}</p>
              <button
                type="button"
                onClick={onResend}
                disabled={resending}
                className="mt-2 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-60"
              >
                {resending ? t("login_resend_sending") : t("login_resend_verification")}
              </button>
            </div>
          )}
          <div className="my-4 flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> o <div className="h-px flex-1 bg-border" />
          </div>
          <button
            type="button"
            disabled={googleLoading}
            onClick={async () => {
              setGoogleLoading(true);
              try {
                const res = await signInGoogle();
                if (res?.error) toast.error(res.error.message);
              } catch (e) {
                toast.error(translateAuthError(e, lang));
              } finally {
                setGoogleLoading(false);
              }
            }}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-3 text-sm font-bold text-foreground shadow-sm hover:bg-accent/30 disabled:opacity-60"
          >
            {googleLoading ? "..." : "Continuar con Google"}
          </button>
          <div className="mt-4 flex items-center justify-between text-xs">
            <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground">
              ¿Olvidaste tu contraseña?
            </Link>
            <Link to="/registro" className="font-bold text-primary hover:underline">
              Crear cuenta
            </Link>
          </div>
          <LegalFooter />
        </div>
      </div>
    </main>
  );
}
